import { useTranslation } from "@sagot/i18n";
import { Icon, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useMembers } from "../../../roles/use-roles";
import { usePermissions } from "../../../session/permissions";
import { PageHeader } from "../shared/page-header";
import { BookingsPanel } from "./bookings-panel";
import { LeadSheet } from "./lead-sheet";
import { LeadStatsRow } from "./lead-stats";
import type { Lead, Stage } from "./lead-types";
import { LeadsActions } from "./leads-actions";
import { LeadsPanel } from "./leads-panel";
import { StageEditorDialog } from "./stage-editor-dialog";
import { useLeadsRoute } from "./use-leads-route";
import { usePatchLead, useStages } from "./use-leads";

/** Moves a lead with an optimistic stage override (board + table) and announces the result. */
function useMoveLead() {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const patch = usePatchLead();
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [announcement, setAnnouncement] = useState("");
  const move = (lead: Lead, stage: Stage) => {
    setOverrides((current) => ({ ...current, [lead.id]: stage.id }));
    patch.mutate(
      { id: lead.id, patch: { stage_id: stage.id } },
      {
        onSuccess: () => {
          const message = t("engage.leads.movedTo", { name: lead.name, stage: stage.name });
          setAnnouncement(message);
          toast.success(message);
        },
        onError: (error) => toast.error(errorMessage(error)),
        onSettled: () => {
          setOverrides(({ [lead.id]: _done, ...rest }) => rest);
        },
      },
    );
  };
  return { move, overrides, announcement };
}

/** `/w/:ws/leads/:leadId?` — Leads & inquiries (mockups/tenant/leads.html), pipeline board and bookings. */
export function LeadsPage() {
  const { t } = useTranslation();
  const route = useLeadsRoute();
  const permissions = usePermissions();
  const stages = useStages();
  const members = useMembers();
  const mover = useMoveLead();
  const [editingStages, setEditingStages] = useState(false);
  const writable = !permissions.readOnly;
  const canEdit = writable && permissions.can("leads.edit");
  const canExport = permissions.can("leads.export");
  const canEditStages = writable && permissions.can("workspace.manage");
  const nameOf = (userId: string | null | undefined) => (userId ? (members.data?.find((m) => m.user_id === userId)?.name ?? null) : null);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title={t("engage.leads.title")}
        subtitle={t("engage.leads.subtitle")}
        actions={
          <LeadsActions
            stageId={route.filters.stageId}
            canExport={canExport}
            canEdit={canEdit}
            canEditStages={canEditStages && Boolean(stages.data)}
            onEditStages={() => {
              setEditingStages(true);
            }}
          />
        }
      />
      {route.view === "bookings" ? (
        <BookingsPanel route={route} canEdit={canEdit} />
      ) : (
        <>
          <LeadStatsRow />
          <LeadsPanel route={route} stages={stages} canEdit={canEdit} overrides={mover.overrides} onMove={mover.move} />
        </>
      )}
      <p className="flex items-center gap-2 text-xs text-fg-subtle">
        <Icon name="shield-check" className="size-3.5 shrink-0" />
        {t("engage.leads.footnote")}
      </p>
      <p className="sr-only" aria-live="polite">
        {mover.announcement}
      </p>
      <LeadSheet leadId={route.leadId} onClose={route.closeLead} stages={stages.data ?? []} canEdit={canEdit} nameOf={nameOf} />
      {stages.data ? <StageEditorDialog open={editingStages} onOpenChange={setEditingStages} stages={stages.data} /> : null}
    </div>
  );
}
